import { useGetReviewerPermissionRoles } from '@exam-center/admin-proctor-management';
import {
  AdminSearchMyExamCenterProctorRequestsStep,
  type AdminSearchMyExamCenterProctorRequestsQuery,
  ExamCenterProctorRequestVerdictStep,
  useAdminSearchMyExamCenterProctorRequestsQuery,
  Verdict,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  DescriptionOutlinedIcon,
  NoRowsOverlay,
  type GridEventListener,
  type GridRowParams,
} from '@shared/components';

import {
  BaseAdminProctorRequestList,
  inReviewColumns,
  supervisorInReviewColumns,
  type InReviewProctorRequest,
} from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useAdminProctorRequestListSearchParams } from '../../hooks';

type ExamCenterProctorRequest =
  AdminSearchMyExamCenterProctorRequestsQuery['ecAdminSearchMyExamCenterProctorRequests']['examCenterProctorRequests'][number];

function getInReviewAdminProctorRequestListRows(
  examCenterProctorRequests: ExamCenterProctorRequest[],
) {
  return examCenterProctorRequests.map((item) => {
    const firstReviewVerdict = item.verdicts?.find(
      (v) =>
        v.step === ExamCenterProctorRequestVerdictStep.InReview_1 &&
        v.requestedAt.getTime() === item.requestedAt?.getTime() &&
        v.submittedAt &&
        v.verdict === Verdict.Approved,
    )?.verdict;

    const secondReviewVerdict = item.verdicts?.find(
      (v) =>
        v.step === ExamCenterProctorRequestVerdictStep.InReview_2 &&
        v.requestedAt.getTime() === item.requestedAt?.getTime() &&
        v.submittedAt &&
        v.verdict === Verdict.Approved,
    )?.verdict;

    return {
      id: item.id,
      refCode: item.refCode,
      name: [item.titleTh, item.firstNameTh, item.middleNameTh, item.lastNameTh]
        .filter(Boolean)
        .join(' '),
      type: PROCTOR_TYPE_VARIANT_MAP[item.type],
      status: 'in-review',
      requestedAt: item.requestedAt,
      examCenter: item.examCenterCode,
      firstReviewVerdict: firstReviewVerdict || 'PENDING',
      secondReviewVerdict:
        firstReviewVerdict === Verdict.Approved && !secondReviewVerdict
          ? 'PENDING'
          : secondReviewVerdict,
    };
  });
}

export default function InReviewAdminProctorRequestList() {
  const [
    { page, pageSize, sortOrder, proctorType, examCenter, search },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminProctorRequestListSearchParams();
  const navigate = useNavigate();

  const { roles } = useGetReviewerPermissionRoles();

  const { data, loading } = useAdminSearchMyExamCenterProctorRequestsQuery({
    variables: {
      adminSearchExamCenterProctorRequestsInput: {
        filter: {
          step: AdminSearchMyExamCenterProctorRequestsStep.InReview,
          fullName: search,
          ...(examCenter.length > 0 ? { examCenterCodes: examCenter } : {}),
          ...(proctorType.length > 0 ? { types: proctorType } : {}),
        },
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'requestedAt',
          type: ORDER_TYPE[sortOrder],
        },
      },
    },
  });

  const examCenterProctorRequests =
    data?.ecAdminSearchMyExamCenterProctorRequests.examCenterProctorRequests ?? [];

  const totalCount = data?.ecAdminSearchMyExamCenterProctorRequests.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<InReviewProctorRequest>,
  ) => {
    navigate(`/admin/exam-proctors/requests/:requestId`, {
      params: {
        requestId: params.row.id,
      },
    });
  };

  const inReviewAdminProctorRequestListRows =
    getInReviewAdminProctorRequestListRows(examCenterProctorRequests);

  const isSearchingOrFiltering =
    search.length > 0 || proctorType.length > 0 || examCenter.length > 0;

  return (
    <BaseAdminProctorRequestList
      columns={roles.has('supervisor') ? supervisorInReviewColumns : inReviewColumns}
      loading={loading}
      rows={inReviewAdminProctorRequestListRows}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return (
            <NoRowsOverlay
              title='ยังไม่มีคำขอผู้คุมสอบที่อยู่ระหว่างอนุมัติ'
              icon={DescriptionOutlinedIcon}
            />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(Number.parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
